import { useSuggestedAddresses } from '@monofront/business/hooks';
import {
  IAddressV2,
  IGetAddressesWithDefaultOneQueryResult,
  ISuggestedAddress,
} from '@monofront/types';
import Button from '@monofront/design-system/components/Button';
import AutoComplete, {
  IAutoComplete,
} from '@monofront/design-system/components/Form/AutoComplete';
import InformativeBox from '@monofront/design-system/components/InformativeBox';
import Input from '@monofront/design-system/components/Form/Input';
import Label from '@monofront/design-system/components/Form/Label';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { RichText } from 'prismic-reactjs';
import { useQuery } from '@apollo/client';
import { GET_ADDRESSES_WITH_DEFAULT_ONE } from '@monofront/business/queries';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import { IPrismicProductEligibilityModal } from './types';
import { useEligibility } from '@monofront/business/contexts';
import getConfig from 'next/config';
import useI18n from '@hooks/i18n/useI18n';
import { v4 as uuid } from 'uuid';
import getThemeColor from '@monofront/design-system/utils/getThemeColor';

const AddressAutoComplete = AutoComplete as IAutoComplete<{
  street: string;
  postcode: string;
  city: string;
}>;

interface IFreshAddressFormProps {
  onSuggestedAddressSelect: (address: ISuggestedAddress | IAddressV2) => void;
  document?: IPrismicProductEligibilityModal;
}

const {
  publicRuntimeConfig: { mapboxToken },
} = getConfig();
const sessionToken = uuid();

const FreshAddressForm: React.FC<IFreshAddressFormProps> = ({
  onSuggestedAddressSelect,
  document,
}) => {
  const tFresh = useTranslations('Fresh');
  const tAccount = useTranslations('Account');
  const { eligibility } = useEligibility();
  const { locale, availableCountryLocale } = useI18n();
  const isUserEligible = Object.values(eligibility).some((value) => value);

  const {
    register,
    formState: { errors },
    handleSubmit,
    setError,
    setValue,
  } = useForm({
    mode: 'onChange',
  });
  const { data } = useQuery<IGetAddressesWithDefaultOneQueryResult>(
    GET_ADDRESSES_WITH_DEFAULT_ONE,
    {
      ssr: false,
      fetchPolicy: 'cache-and-network',
    },
  );
  const addresses = data?.getAddressesWithDefaultOne;

  const {
    writtenStreet,
    setWrittenStreet,
    suggestedAddresses,
    isSuggestedAddressLoading,
  } = useSuggestedAddresses({
    mapboxToken,
    locale,
    sessionToken,
    searchableCountry: availableCountryLocale,
  });

  const onSubmit = () => {
    // display an error on press enter as no address has been selected
    setError('street', {
      type: 'addressNotSelected',
      message: tFresh('errorAddressNotSelected'),
    });
  };

  const onSelect = (address: ISuggestedAddress | IAddressV2) => {
    const { street, postcode, city } = address;

    setValue('street', `${street}, ${postcode}, ${city}`);

    setTimeout(() => {
      // Let the user see the address he has selected
      onSuggestedAddressSelect(address);
    }, 600);
  };

  if (!document) return <HeartLoader />;

  return (
    <>
      <InformativeBox
        backgroundColor={getThemeColor(
          isUserEligible
            ? document.eligible_user_informative_box_background_color
            : document.informative_box_background_color,
        )}
        img={
          isUserEligible
            ? document.eligible_user_informative_box_picto
            : document.informative_box_picto
        }
        hasImageOnMobile
      >
        <RichText
          render={
            isUserEligible
              ? document.eligible_user_informative_box_text
              : document.informative_box_text
          }
        />
      </InformativeBox>
      <form className="form" onSubmit={handleSubmit(onSubmit)}>
        <Label id="street">{tFresh('addressInputLabel')}</Label>
        <AddressAutoComplete
          isLoading={isSuggestedAddressLoading}
          options={
            writtenStreet?.length
              ? suggestedAddresses?.map(
                  ({ label, street, postcode, city, type }) => ({
                    id: label,
                    label,
                    value: {
                      street,
                      postcode,
                      city,
                      type,
                    },
                  }),
                )
              : undefined
          }
          handleSelect={onSelect}
          renderInput={(params) => (
            <Input
              {...params}
              id="street"
              autocomplete="off"
              type="text"
              placeholder={tAccount('suggestedAddressPlaceholder')}
              rounded
              hasError={Boolean(errors.street)}
              {...register('street', {
                required: {
                  value: true,
                  message: tAccount('suggestedAddressRequiredMessage'),
                },
              })}
              onChange={(e) => setWrittenStreet(e.target.value)}
              dataTestId="AddressForm-Input-street"
            />
          )}
        />
        {errors.street && (
          <p
            data-testid="FreshAddressForm-Input-error"
            className="error tw-lf-error-message"
          >
            {errors.street.message as string}
          </p>
        )}
      </form>

      {addresses && addresses.length > 0 && (
        <div className="saved-addresses">
          <p className="hxm">{tFresh('savedAddresses')}</p>
          {addresses?.map((address) => (
            <Button
              key={address.id!}
              variant="transparent"
              size="small"
              hasMinWidth={false}
              block={false}
              onClick={() => onSelect(address)}
            >
              {tFresh('completeAddress', {
                street: address.street,
                postcode: address.postcode,
                city: address.city,
              })}
            </Button>
          ))}
        </div>
      )}

      <style jsx>{`
        .form {
          margin-top: var(--space-4);
        }
        .form :global(label) {
          font-weight: var(--font-weight-base);
          margin-bottom: var(--space-1);
        }
        .form :global(.error) {
          font-size: var(--font-size-1);
          margin-left: var(--space-4);
        }
        .saved-addresses {
          margin-top: var(--space-5);
        }
        .saved-addresses :global(button) {
          margin-top: var(--space-4);
          font-weight: var(--font-weight-light);
        }
      `}</style>
    </>
  );
};

export default FreshAddressForm;
